/**
 * apps/server/client — Changement de mot de passe (sur le SERVEUR)
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * ─── POURQUOI CETTE PAGE EXISTE ICI ──────────────────────────────────────────
 * Le serveur EXIGE que le mot de passe par défaut (`admin`/`admin`) soit changé. Jusqu'ici, il
 * n'offrait aucun moyen de le faire : l'écran vivait dans la console d'administration.
 *
 * Or la console d'administration n'est PAS livrée au client — elle reste chez le prestataire. Le
 * client se retrouvait donc devant un logiciel qui réclame une action et n'en donne pas le moyen :
 * une impasse pure. Aucune subtilité d'architecture ne justifie ça. La règle « le serveur n'est pas
 * une surface d'écriture » vaut pour la CONFIGURATION du site ; elle ne peut pas s'étendre au point
 * d'interdire à un utilisateur de changer SON PROPRE mot de passe sur la machine où il est connecté.
 *
 * L'API existait déjà (`POST /api/users/change-password`, qui vérifie l'ancien mot de passe et
 * baisse le drapeau A4). Il ne manquait que l'écran.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

/** Longueur minimale. Court, mais on refuse surtout de laisser remettre `admin`. */
const MIN = 8;

export default function ChangePassword() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  const [actuel, setActuel] = useState('');
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [erreur, setErreur] = useState('');
  const [ok, setOk] = useState(false);
  const [envoi, setEnvoi] = useState(false);

  const valider = () => {
    if (nouveau.length < MIN) return `Le nouveau mot de passe doit faire au moins ${MIN} caractères.`;
    if (nouveau !== confirmation) return 'La confirmation ne correspond pas.';
    // Interdire de « changer » pour la même chose : sans ça, on pourrait satisfaire l'exigence en
    // resaisissant `admin` — le drapeau retomberait et la protection n'aurait rien protégé.
    if (nouveau === actuel) return 'Le nouveau mot de passe doit être différent de l’actuel.';
    return '';
  };

  const soumettre = async (e) => {
    e.preventDefault();
    const probleme = valider();
    if (probleme) { setErreur(probleme); return; }

    setEnvoi(true); setErreur('');
    try {
      const r = await axios.post('/api/users/change-password', {
        currentPassword: actuel,
        newPassword: nouveau,
      });
      if (!r.data?.success) { setErreur(r.data?.error || 'Échec du changement.'); return; }

      // Le drapeau retombe TOUT DE SUITE. Le jeton reste valide : on ne déconnecte pas quelqu'un
      // qui vient de faire exactement ce qu'on lui demandait.
      updateUser({ mustChangePassword: false });
      setOk(true);

      // On renvoie vers un écran où l'utilisateur A LE DROIT d'aller. `/manage` est réservé aux
      // admins/managers : y expédier un agent l'aurait jeté sur « non autorisé » — juste après
      // qu'il ait fait ce qu'on exigeait de lui. Le pire moment pour une porte fermée.
      const retour = (user?.role === 'admin' || user?.role === 'manager') ? '/manage' : '/counter';
      setTimeout(() => navigate(retour), 1500);
    } catch (err) {
      setErreur(err.response?.data?.error || err.message);
    } finally {
      setEnvoi(false);
    }
  };

  const S = {
    page: { minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    carte: { width: '100%', maxWidth: 420, background: '#fff', borderRadius: 12, padding: 28, boxShadow: '0 6px 24px rgba(0,0,0,.12)' },
    champ: { width: '100%', padding: '10px 12px', marginBottom: 14, borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '1rem' },
    bouton: { width: '100%', padding: '11px 16px', borderRadius: 8, border: 0, background: '#1d4ed8', color: '#fff', fontWeight: 700, fontSize: '1rem', cursor: 'pointer' },
    alerte: { background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 8, padding: 12, marginBottom: 18, fontSize: '.9rem' },
    erreur: { background: '#FEE2E2', border: '1px solid #EF4444', borderRadius: 8, padding: 10, marginBottom: 14, fontSize: '.9rem' },
    succes: { background: '#DCFCE7', border: '1px solid #16A34A', borderRadius: 8, padding: 12, fontSize: '.95rem' },
  };

  if (ok) {
    return (
      <div style={S.page}>
        <div style={S.carte}>
          <h2>Mot de passe modifié</h2>
          <div style={S.succes}>
            ✅ C’est fait. Votre nouveau mot de passe est actif immédiatement — vous restez connecté.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <form style={S.carte} onSubmit={soumettre}>
        <h2 style={{ marginTop: 0 }}>Changer le mot de passe</h2>
        <p style={{ color: '#64748b', marginTop: 0 }}>Compte : <b>{user?.username}</b></p>

        {user?.mustChangePassword && (
          <div style={S.alerte}>
            🔑 Ce compte utilise encore le <b>mot de passe par défaut</b>. Tant qu’il n’est pas
            changé, toute personne ayant accès au réseau peut se connecter en administrateur.
          </div>
        )}

        <label>Mot de passe actuel</label>
        <input style={S.champ} type="password" value={actuel} autoFocus
               onChange={(e) => setActuel(e.target.value)} autoComplete="current-password" />

        <label>Nouveau mot de passe ({MIN} caractères minimum)</label>
        <input style={S.champ} type="password" value={nouveau}
               onChange={(e) => setNouveau(e.target.value)} autoComplete="new-password" />

        <label>Confirmer le nouveau mot de passe</label>
        <input style={S.champ} type="password" value={confirmation}
               onChange={(e) => setConfirmation(e.target.value)} autoComplete="new-password" />

        {erreur && <div style={S.erreur}>{erreur}</div>}

        <button style={S.bouton} type="submit" disabled={envoi || !actuel || !nouveau || !confirmation}>
          {envoi ? 'Modification…' : 'Changer le mot de passe'}
        </button>
      </form>
    </div>
  );
}
